import SearchPage from 'pages/app/mobile/search'
import useApiService from 'services/api'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import { SEARCH_TYPE_LABELS } from 'components/MobileSearch/hooks/searchTypeConstants'
import type { SearchType } from 'components/MobileSearch/hooks/useMobileSearchAPI'

// Contacts attach at the company (chain) level — same as desktop — so this step
// searches companies only (First Bite + custom), never doors/locations. Besides
// matching desktop, it removes the door results that couldn't resolve to a
// contact_company and so never enabled the wizard's "Next".
const COMPANY_SEARCH_OPTIONS: { value: SearchType; label: string }[] = [
  { value: 'fb_company', label: SEARCH_TYPE_LABELS.fb_company },
  { value: 'custom_company', label: SEARCH_TYPE_LABELS.custom_company },
]

// First Bite 2-letter type code for restaurants. Only restaurants resolve to a
// contact_company on mobile today (via the restaurant chain endpoint); other FB
// verticals (K-12 "DI", hospitals, etc.) have no resolver here, so they're
// hidden to avoid a selection that can't enable Submit.
const RESTAURANT_TYPE_CODE = 'RE'

/**
 * Keep only results this step can resolve to a contact_company: First Bite
 * restaurants and custom (tenant-created) companies, which carry
 * contact_company_id directly.
 */
function isResolvableCompany(
  result: ContactCompanySearchResult | DoorSearchResult
): boolean {
  const company = result as ContactCompanySearchResult
  return company.is_unmatched === true || company.type === RESTAURANT_TYPE_CODE
}

export type ResolvedCompany = {
  id: number
  name: string
  domain?: string | null
}

/**
 * Resolve a chosen company search result to the `contact_company` the create
 * payload needs. Custom companies carry `contact_company_id` directly; First
 * Bite chain results omit it, so we look it up by chain id.
 */
export function useResolveForCompany(
  selected?: ContactCompanySearchResult | DoorSearchResult
): ResolvedCompany | undefined {
  const api = useApiService()
  // The search is company-only (see COMPANY_SEARCH_OPTIONS), so a selection is
  // always a ContactCompanySearchResult.
  const company = selected as ContactCompanySearchResult | undefined
  const directId = company?.contact_company_id

  const parsedChainId =
    company && directId == null ? Number(company.item_id) : NaN
  const chainId =
    Number.isInteger(parsedChainId) && parsedChainId > 0
      ? parsedChainId
      : undefined
  const { data: chainData } = api.useGetChain(chainId)

  if (!company) return undefined
  if (directId != null) {
    return { id: directId, name: company.name, domain: company.domain }
  }
  if (chainData && chainData.contact_company_id != null) {
    return {
      id: chainData.contact_company_id,
      name: chainData.chain,
      domain: chainData.domain,
    }
  }
  return undefined
}

/**
 * Wizard step: company-only mobile search. Tapping a result selects/highlights
 * it (single-select) instead of proceeding; the wizard's footer consumes
 * `selectedResult`.
 */
export default function SelectCompanyStep({
  initialQuery,
  selectedResult,
  onSelectResult,
}: {
  initialQuery?: string
  selectedResult?: ContactCompanySearchResult | DoorSearchResult
  onSelectResult: (r: ContactCompanySearchResult | DoorSearchResult) => void
}) {
  return (
    <SearchPage
      hideBack
      options={COMPANY_SEARCH_OPTIONS}
      resultFilter={isResolvableCompany}
      initialQuery={initialQuery}
      selectedItemId={selectedResult?.item_id}
      onClick={onSelectResult}
    />
  )
}
